import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import PageMeta from "../components/common/PageMeta";
import { useAuth } from "../context/AuthContext";
import Button from "../components/ui/button/Button";
import Input from "../components/form/input/InputField";
import Label from "../components/form/Label";

export default function UserProfiles() {
  const navigate = useNavigate();
  const { user, updateProfile, uploadProfileImage, changePassword } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  });
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Site Managers must use @sbmnyc.com email
  const isSiteManager = user?.user_type === 1;
  const isSbmEmail = (email: string) => email.toLowerCase().endsWith("@sbmnyc.com");

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
      });
      setPreviewImage(user.image || user.image_url || null);
    }
  }, [user]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSaveError(null);
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
    setPasswordError(null);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
      setSaveError('Please select a JPEG, PNG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setSaveError('Image must be less than 5MB');
      return;
    }

    setPendingImageFile(file);
    setPreviewImage(URL.createObjectURL(file));
    setSaveError(null);
  };

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    setSaveError(null);

    // Site Managers must use @sbmnyc.com email
    if (isSiteManager && !isSbmEmail(formData.email)) {
      setSaveError("Site Managers must use an @sbmnyc.com email address");
      return;
    }

    setIsSaving(true);
    try {
      // Upload image first if one was selected
      if (pendingImageFile) {
        await uploadProfileImage(pendingImageFile);
        setPendingImageFile(null);
      }

      const updateData: { name: string; email: string; username: string } = {
        name: formData.name,
        email: formData.email,
        username: formData.email,
      };

      await updateProfile(updateData);
      showToast("Profile updated successfully");
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordSave = async () => {
    setPasswordError(null);

    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (passwordData.new_password.length < 8) {
      setPasswordError("New password must be at least 8 characters");
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword(passwordData);
      setPasswordData({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
      });
      showToast("Password changed successfully");
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : "Failed to change password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <PageMeta
        title="Edit Profile | SBM Package Admin"
        description="Edit your profile"
      />

      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <button
            onClick={() => navigate("/sites")}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-gray-800 dark:text-white">
            Profile
          </h1>
        </div>

        {/* Form Card */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03] lg:p-8">

          {/* Profile Photo Section */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-12">
            <div>
              <h3 className="text-base font-medium text-gray-800 dark:text-white">
                Profile Photo
              </h3>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-6">
                <button
                  type="button"
                  onClick={handleImageClick}
                  className="relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-brand-500 text-white text-2xl font-semibold hover:opacity-90 transition-opacity"
                >
                  {previewImage ? (
                    <img
                      src={previewImage}
                      alt="Profile"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    user?.name ? getInitials(user.name) : "U"
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity">
                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <div>
                  <Button variant="outline" size="sm" onClick={handleImageClick}>
                    Change Photo
                  </Button>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    JPG, PNG or WebP. Max 5MB.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="my-8 border-t border-gray-200 dark:border-gray-700" />

          {/* Error alert for profile */}
          {saveError && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
              {saveError}
            </div>
          )}

          {/* Personal Information Section */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-12">
            <div>
              <h3 className="text-base font-medium text-gray-800 dark:text-white">
                Personal Information
              </h3>
            </div>
            <div className="lg:col-span-2">
              <div className="space-y-5">
                <div>
                  <Label>Name</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Enter your name"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="my-8 border-t border-gray-200 dark:border-gray-700" />

          {/* Contact Section */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-12">
            <div>
              <h3 className="text-base font-medium text-gray-800 dark:text-white">
                Contact
              </h3>
            </div>
            <div className="lg:col-span-2">
              <div className="space-y-5">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="Enter your email"
                  />
                  {isSiteManager && (
                    <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                      Site Managers must use an @sbmnyc.com email address.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="my-8 border-t border-gray-200 dark:border-gray-700" />

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>

        {/* Password Card */}
        <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03] lg:p-8">

          {/* Error alert for password */}
          {passwordError && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
              {passwordError}
            </div>
          )}

          {/* Password Section */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-12">
            <div>
              <h3 className="text-base font-medium text-gray-800 dark:text-white">
                Change Password
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Update your password to keep your account secure.
              </p>
            </div>
            <div className="lg:col-span-2">
              <div className="space-y-5">
                <div>
                  <Label>Current Password</Label>
                  <Input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => handlePasswordChange("current_password", e.target.value)}
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => handlePasswordChange("new_password", e.target.value)}
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.new_password_confirmation}
                    onChange={(e) => handlePasswordChange("new_password_confirmation", e.target.value)}
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="my-8 border-t border-gray-200 dark:border-gray-700" />

          {/* Change Password Button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={handlePasswordSave}
              disabled={isChangingPassword || !passwordData.current_password || !passwordData.new_password || !passwordData.new_password_confirmation}
            >
              {isChangingPassword ? "Changing..." : "Change Password"}
            </Button>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed top-6 right-6 z-50 animate-fade-in-out rounded-lg bg-green-600 px-5 py-3 text-sm font-medium text-white shadow-lg">
          {toast}
        </div>
      )}
    </>
  );
}
